import { useEffect, useMemo, useRef, useState } from 'react';
import { getAppVersion, isBrowser } from '@croquiscom/web2app';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { themeTokens } from '@croquiscom-pvt/zds/tokens/zigzag';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';

import { useOnceReveal } from '@common/hooks';
import { GeneralArrowRightIcon } from '@common/icons';
import { Color, imageSize } from '@common/styles';
import { PDPCustomAttributeKeys } from '@common/test-automation';
import { BaseText } from '@common/toolbox';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useCatalogProductIdParams } from '@domains/pdp-common';

import arrow_icon from './images/ic-arrow.svg';
import disabled_arrow_icon from './images/ic-arrow--disabled.svg';
import { SelectBoxItem, SelectBoxItemOptionValue as OptionValue } from './select-box-item';
import { SoldOutOptionValue, SoldOutSelectBoxItem } from './sold-out-select-box-item';

interface Props {
  className?: string;
  disabledPrice?: boolean;
  name: string;
  pdpUrl?: string;
  options?: SoldOutOptionValue[] | OptionValue[];
  active?: boolean;
  isAccessible?: boolean;
  isLastOption?: boolean;
  isFirstSoldOutOption?: boolean;
  disabledAnimation?: boolean;
  disabledNotification?: boolean;
  isMetaLinkAvailable?: boolean;
  isZigzin?: boolean;
  isMatchingProduct?: boolean;
  is_bogo?: boolean;
  hasFastShippingOption?: boolean;
  onClick?: () => void;
  onSelect?: (option: OptionValue) => void;
  onImpress?: (value: string, is_matching_option: boolean) => void;
  onImpressSoldout?: (value: string) => void;
  onRestockNotiClick?: (option: OptionValue, active: boolean) => void;
  onImpressMetaPDPLink?: () => void;
  onMetaPDPLinkClick?: () => void;
  onOptionHeightChange?: (open: boolean) => void;
}

const SELECT_BOX_BORDER_WIDTH = 2;

// MEMO: 블프 임시 대응 코드, 블프 끝나고 제거 예정 (특정 상품은 무조건 알림 버튼 미노출)
const EXCLUDED_PRODUCT_ID = '152502667';

/**
 * @public
 */
export const SelectBox = ({
  name,
  options = [],
  active = false,
  isAccessible = true,
  isFirstSoldOutOption = true,
  disabledAnimation = false,
  disabledNotification = false,
  isLastOption = false,
  is_bogo = false,
  hasFastShippingOption = false,
  isZigzin = false,
  isMatchingProduct = false,
  isMetaLinkAvailable = false,
  disabledPrice,
  onClick = () => undefined,
  onSelect = () => undefined,
  onImpress,
  onImpressSoldout,
  onRestockNotiClick = () => undefined,
  onImpressMetaPDPLink,
  onMetaPDPLinkClick = () => undefined,
  onOptionHeightChange,
}: Props) => {
  const [open, setOpen] = useState(isAccessible);
  const { targetRef, isRevealed } = useOnceReveal();
  const country = 'KR';
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { catalog_product_id } = useCatalogProductIdParams(); // MEMO: 블프 임시 대응 코드, 블프 끝나고 제거 예정 (특정 상품은 알림 버튼 미노출)
  const should_show_button =
    !disabledNotification && !is_bogo && isZigzin && country === 'KR' && EXCLUDED_PRODUCT_ID !== catalog_product_id;
  const duration = disabledAnimation ? 0 : 0.25;
  const optionsByStatus = useMemo(() => {
    return {
      onSales: options.filter((option) => !option.sold_out),
      soldout: options.filter((option) => option.sold_out) as SoldOutOptionValue[],
    };
  }, [options]);

  const isMetaLinkVisible = useMemo(
    () =>
      isBrowser() &&
      Boolean(getAppVersion()) &&
      isZigzin &&
      isMetaLinkAvailable &&
      isFirstSoldOutOption &&
      optionsByStatus &&
      optionsByStatus.soldout &&
      optionsByStatus.soldout.length > 0 &&
      country === 'KR',
    [isZigzin, isMetaLinkAvailable, isFirstSoldOutOption, optionsByStatus, country],
  );
  const handleClick = () => {
    onClick();
    if (active) {
      setOpen((isOpen) => !isOpen);
    }
  };

  useEffect(() => {
    if (isRevealed && onImpressMetaPDPLink) {
      onImpressMetaPDPLink();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevealed]);

  useEffect(() => {
    if (active) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [active]);

  return (
    <Container ref={containerRef} active={open}>
      <DisplayBox
        active={open}
        onClick={handleClick}
        data-allow-preview
        data-custom-ta-key={PDPCustomAttributeKeys.PDP_PRODUCT_OPTIONS_12}
      >
        <BaseText
          color={isAccessible ? themeTokens.v3Color.gray900.value.light : themeTokens.v3Color.gray300.value.light}
          variation={'BODY_15_SEMIBOLD'}
        >
          {name.replace(/&nbsp;|&#160;/g, ' ')}
        </BaseText>
        <ArrowIcon active={open} isAccessible={isAccessible} />
      </DisplayBox>
      {open && (
        <TotalOptionList
          initial={!disabledAnimation ? { opacity: 0, height: 0 } : {}}
          animate={{
            opacity: 1,
            height: 'auto',
            visibility: 'visible',
          }}
          exit={{
            opacity: 0,
            height: 0,
            visibility: 'hidden',
          }}
          transition={{ duration, ease: 'easeInOut' }}
          onAnimationComplete={() => {
            onOptionHeightChange?.(open);
          }}
        >
          {optionsByStatus.onSales.length > 0 && (
            <ListContainer>
              {optionsByStatus.onSales
                .filter((option) => !option.sold_out)
                .map((option, index) => (
                  <SelectBoxItem
                    key={`${index}_${option.value}`}
                    option={option}
                    onSelect={onSelect}
                    onImpress={onImpress}
                    is_zigzin_product={isZigzin}
                    isBogo={is_bogo}
                    isMatchingProduct={isMatchingProduct}
                    hasFastShippingOption={hasFastShippingOption}
                    disabledPrice={disabledPrice}
                  />
                ))}
            </ListContainer>
          )}
          {optionsByStatus.soldout.length > 0 && (
            <SoldoutListContainer
              backgroundColor={
                isFirstSoldOutOption ? themeTokens.v3Color.gray50.value.light : themeTokens.v3Color.white.value.light
              }
            >
              {optionsByStatus.soldout.map((option, idx) => (
                <SoldOutSelectBoxItem
                  key={`${option.value}_${idx}`}
                  option={option}
                  isLastOption={isLastOption}
                  isFirstSoldOutOption={isFirstSoldOutOption}
                  is_zigzin_product={isZigzin}
                  isMatchingProduct={isMatchingProduct}
                  shouldShowButton={should_show_button}
                  onSelect={onSelect}
                  onImpress={onImpressSoldout}
                  onRestockNotiClick={onRestockNotiClick}
                  hasFastShippingOption={hasFastShippingOption}
                  disabledPrice={disabledPrice}
                />
              ))}
              {isMetaLinkVisible && (
                <MoveToMetaPdp onClick={onMetaPDPLinkClick} data-allow-preview ref={targetRef}>
                  일반배송으로 구매하기 <GeneralArrowRightIcon color={vars.v3Color.gray700} width={7.5} height={15} />
                </MoveToMetaPdp>
              )}
            </SoldoutListContainer>
          )}
        </TotalOptionList>
      )}
    </Container>
  );
};

const Container = styled.div<{ active: boolean }>`
  margin-bottom: 8px;
  overflow: hidden;
  box-sizing: border-box;
  border: ${`${SELECT_BOX_BORDER_WIDTH}px`} solid ${vars.v3Color.gray150};
  border-radius: 8px;
  ${({ active }) =>
    active &&
    `
      border-color: ${vars.v3Color.gray200};
      opacity: 1;
    `}
`;

const TotalOptionList = styled(motion.div)``;

const ListContainer = styled.ul``;

const SoldoutListContainer = styled.ul<{ backgroundColor: Color }>`
  background: ${({ backgroundColor }) => backgroundColor};
  background-color: ${({ backgroundColor }) => backgroundColor};

  li:first-of-type {
    border: none;
  }
`;

const DisplayBox = styled.div<{ active: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 42px 0 16px;
  box-sizing: border-box;
  border-bottom: 1px solid transparent;
  cursor: pointer;

  ${({ active }) => active && `border-color: ${vars.v3Color.gray200};`}
  span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    word-break: break-all;
  }
`;

const ArrowIcon = styled.figure<{ active: boolean; isAccessible: boolean }>`
  position: absolute;
  top: 50%;
  right: 16px;
  ${imageSize('16px', '8px')};
  background: ${({ isAccessible }) => `transparent url(${isAccessible ? arrow_icon : disabled_arrow_icon});`};
  transform: ${({ active }) => (active ? 'translateY(calc(-50%)) rotate(180deg);' : 'translateY(-50%);')};
`;

const MoveToMetaPdp = styled.li`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 48px;
  background: transparent;
  font-weight: 600;
  font-size: 13px;
  color: ${vars.v3Color.gray700};
  border-top: 1px solid ${vars.v3Color.gray100};

  svg {
    margin-left: 3px;
    margin-top: -1px;
  }
`;
